/* eslint-disable @typescript-eslint/no-misused-new */
// https://stackoverflow.com/questions/34098999/how-to-convert-new-somefunction-syntax-to-typescript

// Chromecast
// https://developers.google.com/cast/docs/chrome_sender/integrate

/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.CastStateEventData */
export interface CastStateEventData {
  castState: string
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework#.CastContextEventType */
export interface CastContextEventType {
  CAST_STATE_CHANGED: string
  SESSION_STATE_CHANGED: string
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework#.RemotePlayerEventType */
export interface RemotePlayerEventType {
  ANY_CHANGE: string
  IS_CONNECTED_CHANGED: string
  IS_MEDIA_LOADED_CHANGED: string
  QUEUE_DATA_CHANGED: string
  VIDEO_INFO_CHANGED: string
  DURATION_CHANGED: string
  CURRENT_TIME_CHANGED: string
  IS_PAUSED_CHANGED: string
  VOLUME_LEVEL_CHANGED: string
  CAN_CONTROL_VOLUME_CHANGED: string
  IS_MUTED_CHANGED: string
  CAN_PAUSE_CHANGED: string
  CAN_SEEK_CHANGED: string
  DISPLAY_NAME_CHANGED: string
  STATUS_TEXT_CHANGED: string
  TITLE_CHANGED: string
  DISPLAY_STATUS_CHANGED: string
  MEDIA_INFO_CHANGED: string
  IMAGE_URL_CHANGED: string
  PLAYER_STATE_CHANGED: string
  IS_PLAYING_BREAK_CHANGED: string
  NUMBER_BREAK_CLIPS_CHANGED: string
  CURRENT_BREAK_CLIP_NUMBER_CHANGED: string
  CURRENT_BREAK_TIME_CHANGED: string
  CURRENT_BREAK_CLIP_TIME_CHANGED: string
  BREAK_ID_CHANGED: string
  BREAK_CLIP_ID_CHANGED: string
  WHEN_SKIPPABLE_CHANGED: string
  LIVE_SEEKABLE_RANGE_CHANGED: string
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework#.SessionState */
export interface SessionState {
  NO_SESSION: string
  SESSION_STARTING: string
  SESSION_STARTED: string
  SESSION_START_FAILED: string
  SESSION_ENDING: string
  SESSION_ENDED: string
  SESSION_RESUMED: string
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework#.CastState */
export interface CastState {
  NO_DEVICES_AVAILABLE: string
  NOT_CONNECTED: string
  CONNECTING: string
  CONNECTED: string
}
/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast?hl=en#.AutoJoinPolicy */
export interface AutoJoinPolicy {
  TAB_AND_ORIGIN_SCOPED: string
  ORIGIN_SCOPED: string
  PAGE_SCOPED: string
}
/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.Image */
export interface Image {
  height: number
  width: number
  url: string
  new (url: string): Image
}
/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.media.GenericMediaMetadata */
export interface GenericMediaMetadata {
  images: Image[]
  subtitle: string
  title: string
  new (): GenericMediaMetadata
}
/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.media.MediaInfo */
export interface MediaInfo {
  metadata: GenericMediaMetadata
  contentId: string
  streamType: 'BUFFERED' | 'LIVE' | 'OTHER'
  new (contentId: string, contentType: string): MediaInfo
}

/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast#.ReceiverType */
export interface ReceiverType {
  CAST: string
  DIAL: string
  HANGOUT: string
  CUSTOM: string
}
/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.Volume */
export interface Volume {
  level?: number
  muted?: boolean
  stepInterval: number
}
/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.Receiver */
export interface Receiver {
  friendlyName: string
  label: string
  receiverType: ReceiverType
  volume?: Volume
}

/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast#.ErrorCode */
export interface ErrorCode {
  CANCEL: string
  TIMEOUT: string
  API_NOT_INITIALIZED: string
  INVALID_PARAMETER: string
  EXTENSION_NOT_COMPATIBLE: string
  EXTENSION_MISSING: string
  RECEIVER_UNAVAILABLE: string
  SESSION_ERROR: string
  CHANNEL_ERROR: string
  LOAD_MEDIA_FAILED: string
}

/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.media.LoadRequest */
export interface LoadRequest {
  currentTime?: number
  playbackRate?: number
  autoplay: boolean
  new (mediaInfo: MediaInfo): LoadRequest
}

/** This is very incomplete */
export interface MediaSession {
  media: {
    contentId: string
  }
}

/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.CastSession */
export interface CastSession {
  state: SessionState
  getCastDevice: () => Receiver
  loadMedia: (request: LoadRequest) => Promise<ErrorCode | null>
  endSession: (stopCasting: boolean) => void
  getMediaSession: () => MediaSession
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.SessionStateEventData */
export interface SessionStateEventData {
  session: CastSession
  sessionState: string
  opt_errorCode: ErrorCode
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.CastOptions */
export interface CastOptions {
  receiverApplicationId?: string
  language?: string
  autoJoinPolicy?: string
  resumeSavedSession?: boolean
  androidReceiverCompatible?: boolean
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.CastContext */
export interface CastContext {
  getCurrentSession: () => CastSession | null
  getMediaSession: () => CastSession | null
  setOptions: (payload: CastOptions) => void
  addEventListener: (type: string, handler: (event: any) => void) => void
  removeEventListener: (type: string, handler?: (event: any) => void) => void
}
/** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.media.LiveSeekableRange */
export interface LiveSeekableRange {
  end: number | undefined
  isLiveDone: boolean | undefined
  isMovingWindow: boolean | undefined
  start: number | undefined
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayer */
export interface RemotePlayer {
  mediaInfo: MediaInfo
  volumeLevel: number
  canSeek: boolean
  currentTime: number
  isMuted: boolean
  isPaused: boolean
  isConnected: boolean
  displayStatus: string
  displayName: string
  statusText: string
  liveSeekableRange: LiveSeekableRange
  new (): RemotePlayer
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayerChangedEvent */
export interface RemotePlayerChangedEvent {
  field: string
  value: any
}
/** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayerController */
export interface RemotePlayerController {
  addEventListener: (
    type: string,
    handler: (event: RemotePlayerChangedEvent) => void
  ) => void
  removeEventListener: (type: string, handler?: (event: any) => void) => void
  muteOrUnmute: () => void
  playOrPause: () => void
  seek: () => void
  setVolumeLevel: () => void
  stop: () => void
  new (player: RemotePlayer): RemotePlayerController
}
export interface ChromecastFramework {
  framework: {
    /** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.CastContext */
    CastContext: {
      getInstance: () => CastContext
    }
    /** https://developers.google.com/cast/docs/reference/web_sender/cast.framework#.CastContextEventType */
    CastContextEventType: CastContextEventType
    /** https://developers.google.com/cast/docs/reference/web_sender/cast.framework#.CastState */
    CastState: CastState
    /** https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayer */
    RemotePlayer: RemotePlayer
    RemotePlayerController: RemotePlayerController
    RemotePlayerEventType: RemotePlayerEventType
    SessionState: SessionState
  }
}

export interface Chrome {
  cast: {
    /** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast?hl=en#.AutoJoinPolicy */
    AutoJoinPolicy: AutoJoinPolicy
    /** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.Image */
    Image: Image
    media: {
      GenericMediaMetadata: GenericMediaMetadata
      MediaInfo: MediaInfo
      /** https://developers.google.com/cast/docs/reference/web_sender/chrome.cast.media.LoadRequest */
      LoadRequest: LoadRequest
    }
  }
}

export enum ChromecastStatus {
  Disconnected,
  Started,
  Paused,
  Playing,
}
